import { useCallback, useEffect, useState } from 'react';

import { normalizeDashboardRoute } from '@helpers/dashboardLinks';

const STORAGE_KEY = 'adminPinnedSidebarLinks';
const PINS_CHANGED_EVENT = 'admin-sidebar-pins-changed';

function readPinnedRoutes() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeDashboardRoute);
  } catch {
    return [];
  }
}

function writePinnedRoutes(routes) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
  window.dispatchEvent(new CustomEvent(PINS_CHANGED_EVENT));
}

export function getPinnedRoutes() {
  return readPinnedRoutes();
}

export function isRoutePinned(to) {
  const route = normalizeDashboardRoute(to);
  return readPinnedRoutes().includes(route);
}

export function pinRoute(to) {
  const route = normalizeDashboardRoute(to);
  if (isRoutePinned(route)) return;
  writePinnedRoutes([...readPinnedRoutes(), route]);
}

export function unpinRoute(to) {
  const route = normalizeDashboardRoute(to);
  writePinnedRoutes(readPinnedRoutes().filter((pinnedRoute) => pinnedRoute !== route));
}

export function toggleRoutePin(to) {
  if (isRoutePinned(to)) {
    unpinRoute(to);
  } else {
    pinRoute(to);
  }
}

export function subscribeToSidebarPins(callback) {
  const onPinsChanged = () => callback(readPinnedRoutes());
  const onStorage = (event) => {
    if (event.key === STORAGE_KEY) {
      callback(readPinnedRoutes());
    }
  };

  window.addEventListener(PINS_CHANGED_EVENT, onPinsChanged);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(PINS_CHANGED_EVENT, onPinsChanged);
    window.removeEventListener('storage', onStorage);
  };
}

export function useSidebarPins() {
  const [pinnedRoutes, setPinnedRoutes] = useState(getPinnedRoutes);

  useEffect(() => subscribeToSidebarPins(setPinnedRoutes), []);

  const isPinned = useCallback((to) => pinnedRoutes.includes(normalizeDashboardRoute(to)), [
    pinnedRoutes,
  ]);

  const togglePin = useCallback((to) => {
    toggleRoutePin(to);
  }, []);

  return { pinnedRoutes, isPinned, togglePin };
}
